/**
 * FusionAuth Session Bridge Utilities
 *
 * These utilities help establish and manage FusionAuth browser sessions
 * for seamless SSO with GoHighLevel Marketing Hub.
 */

import env from 'environments';

/**
 * Attempts to establish a FusionAuth browser session silently using a hidden iframe.
 *
 * This works when:
 * 1. User previously authenticated with FusionAuth and checked "Keep me signed in"
 * 2. FusionAuth session cookies are still valid
 *
 * If the user doesn't have an existing FusionAuth session, this will fail silently
 * and the user will be prompted to login when accessing Marketing Hub.
 *
 * @param {string} email - User's email address for login_hint
 * @returns {Promise<boolean>} - True if session was established, false otherwise
 */
export const establishFusionAuthSession = (email) => {
  return new Promise((resolve) => {
    // Skip if FusionAuth is not configured
    if (!env.FUSIONAUTH_URL || !env.FUSIONAUTH_APP_ID) {
      console.warn('[FusionAuth] Not configured, skipping session establishment');
      resolve(false);
      return;
    }

    // Skip if GHL is not enabled
    if (!env.GHL_ENABLED) {
      resolve(false);
      return;
    }

    const timeout = 10000; // 10 second timeout
    let timeoutId;
    let iframe;

    // Cleanup function
    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
      window.removeEventListener('message', handleMessage);
    };

    // Handle postMessage from callback
    const handleMessage = (event) => {
      // Verify origin
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type === 'fusionauth-session-established') {
        cleanup();
        console.log('[FusionAuth] Session established successfully');
        resolve(true);
      } else if (event.data?.type === 'fusionauth-session-failed') {
        cleanup();
        console.log('[FusionAuth] Silent auth failed, user will be prompted on Marketing Hub access');
        resolve(false);
      }
    };

    window.addEventListener('message', handleMessage);

    // Create hidden iframe
    iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';

    // Build FusionAuth OAuth URL with prompt=none for silent auth
    const callbackUrl = `${window.location.origin}/auth/fusionauth-silent-callback`;
    const authorizeUrl = new URL('/oauth2/authorize', env.FUSIONAUTH_URL);
    authorizeUrl.searchParams.set('client_id', env.FUSIONAUTH_APP_ID);
    authorizeUrl.searchParams.set('redirect_uri', callbackUrl);
    authorizeUrl.searchParams.set('response_type', 'code');
    authorizeUrl.searchParams.set('scope', 'openid email profile');
    authorizeUrl.searchParams.set('prompt', 'none'); // Silent auth - no UI
    if (email) {
      authorizeUrl.searchParams.set('login_hint', email);
    }

    iframe.src = authorizeUrl.toString();

    // Set timeout
    timeoutId = setTimeout(() => {
      cleanup();
      console.log('[FusionAuth] Silent auth timed out');
      resolve(false);
    }, timeout);

    // Add iframe to DOM
    document.body.appendChild(iframe);
  });
};

/**
 * Check if user likely has a FusionAuth session.
 * This is a hint based on whether they've successfully established a session before.
 *
 * @returns {boolean}
 */
export const hasFusionAuthSessionHint = () => {
  try {
    return localStorage.getItem('fusionauth_session_established') === 'true';
  } catch {
    return false;
  }
};

/**
 * Mark that FusionAuth session was established.
 * This helps us skip unnecessary silent auth attempts.
 */
export const markFusionAuthSessionEstablished = () => {
  try {
    localStorage.setItem('fusionauth_session_established', 'true');
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Clear FusionAuth session hint (e.g., on logout)
 */
export const clearFusionAuthSessionHint = () => {
  try {
    localStorage.removeItem('fusionauth_session_established');
  } catch {
    // Ignore localStorage errors
  }
};
